/**
 * Primary navigation — `nav_shape` is `navbar`, so the entries sit
 * HORIZONTALLY inside the top bar and the category tree hangs off the
 * «التصنيفات» entry as a native disclosure panel.
 *
 * Absent below the tablet tier: the bottom bar carries the same four entries
 * there, which is the third arrangement of one navigation rather than a second
 * navigation.
 *
 * Three tiers, and the middle one drops the ICONS rather than the labels. The
 * line at 768px carries everything the public site's carries plus the
 * notification handle and the account face, so it is tighter here, not looser.
 * The icon is `aria-hidden` and duplicates the text beside it, so dropping it
 * costs the accessibility tree nothing — dropping the label would leave the
 * link with no accessible name. Icons return at the desktop tier.
 *
 * The active entry carries THREE signals, never colour alone: accent ink, an
 * accent rule on the block-end edge, and `aria-current="page"`.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { HIDDEN_SCROLLBAR } from '@shared/ui';

import { GenreTree, type GenreNode } from './GenreTree';
import { chromeCopy } from './copy';
import { IconCaret, IconCategories, IconEditorial, IconHome, IconSearch } from './icons';
import { CROSS_HOST_LINK, navEntries, type NavEntryId } from './nav';

const ITEM_CLASS =
  'flex min-h-11 items-center gap-3 whitespace-nowrap rounded-sm border-b-2 border-transparent px-3 text-sm ' +
  'text-ink-muted no-underline transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text';

const ACTIVE_CLASS = 'border-b-accent font-semibold text-accent-text';

const ICON_CLASS = 'hidden shrink-0 lg:block';

const ENTRY_ICON: Readonly<Record<NavEntryId, (props: { className?: string }) => ReactNode>> = {
  home: IconHome,
  categories: IconCategories,
  search: IconSearch,
  editorial: IconEditorial,
};

export interface NavBarProps {
  /** The genre tree the categories entry discloses. */
  categories?: readonly GenreNode[];
  activeCategorySlug?: string;
}

function CategoriesDisclosure({ categories, activeCategorySlug }: NavBarProps) {
  const [open, setOpen] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const summaryRef = useRef<HTMLElement | null>(null);
  const Icon = ENTRY_ICON.categories;

  useEffect(() => {
    if (!open) return;

    // `<details>` gives open/close and its own state announcement for free.
    // These two are what it does NOT give: `Esc` with focus returned to the
    // summary, and a click outside.
    const onPointerDown = (event: PointerEvent): void => {
      if (!detailsRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      summaryRef.current?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <details
      ref={detailsRef}
      open={open}
      // The wrapper is the positioned ancestor of the panel below it, and it
      // carries NO overflow: setting `overflow-x` alone makes the other axis
      // compute to `auto` by the spec's own rule, which would turn a 44px bar
      // into a clipping box for a panel that hangs beneath it.
      className="relative"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary
        ref={(node) => {
          summaryRef.current = node;
        }}
        className={`${ITEM_CLASS} cursor-pointer list-none [&::-webkit-details-marker]:hidden`}
      >
        <Icon className={ICON_CLASS} />
        <span>{chromeCopy.nav.categories}</span>
        {/* A direction in DISCLOSURE, not in reading: it rotates, never mirrors. */}
        <IconCaret className="transition-transform duration-fast [details[open]_&]:rotate-180" />
      </summary>

      {/*
        THE SCROLLBAR IS HIDDEN, THE SCROLLING IS NOT. `overflow-y` stays
        `auto`, so the wheel, a trackpad, a touch drag, the keyboard and every
        assistive technology reach the overflow exactly as before — only the
        gutter is withdrawn. It was drawn permanently over the panel's own
        reading edge in RTL and read as a border the tree had grown, and with
        the branches folded the panel is short enough that it was dead chrome
        most of the time. The same class the rails already use.
      */}
      <div
        className={`absolute start-0 top-[calc(100%+var(--space-2))] z-[3] max-h-[min(70vh,32rem)] min-w-64 overflow-y-auto rounded-md border border-border bg-surface-overlay p-4 text-ink shadow-elev-3 lg:min-w-80 ${HIDDEN_SCROLLBAR}`}
      >
        <GenreTree nodes={categories} activeSlug={activeCategorySlug} />
      </div>
    </details>
  );
}

export function NavBar({ categories = [], activeCategorySlug }: NavBarProps) {
  return (
    <nav
      aria-label={chromeCopy.primaryNavLabel}
      className="ms-2 hidden min-w-0 items-center gap-1 md:flex lg:ms-6 lg:gap-2"
    >
      {navEntries.map((entry) => {
        const Icon = ENTRY_ICON[entry.id];

        if (entry.kind === 'disclosure') {
          return (
            <CategoriesDisclosure
              key={entry.id}
              categories={categories}
              activeCategorySlug={activeCategorySlug}
            />
          );
        }

        if (entry.kind === 'external') {
          // Served on the public host. It opens BESIDE the app rather than
          // replacing it — see `CROSS_HOST_LINK`.
          return entry.href ? (
            <a key={entry.id} href={entry.href} {...CROSS_HOST_LINK} className={ITEM_CLASS}>
              <Icon className={ICON_CLASS} />
              <span>{entry.label}</span>
            </a>
          ) : null;
        }

        // A route entry always carries one; the null case belongs to the
        // categories disclosure, which is handled above.
        if (entry.href === null) return null;

        return (
          <NavLink
            key={entry.id}
            to={entry.href}
            end
            className={({ isActive }) => (isActive ? `${ITEM_CLASS} ${ACTIVE_CLASS}` : ITEM_CLASS)}
          >
            <Icon className={ICON_CLASS} />
            <span>{entry.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
